/**
 * useHiddenWidgets Hook
 *
 * Simple localStorage-based state for hiding dashboard widgets.
 * No backend persistence - just local preference.
 */

import { useState, useCallback } from 'react'

const STORAGE_KEY = 'dashboard-hidden-widgets'

/**
 * Widget IDs that can be hidden
 */
export type WidgetId =
  | 'metrics'
  | 'pipeline'
  | 'tasks'
  | 'calls'
  | 'email'
  | 'discussions'
  | 'activity'
  | 'events'

/**
 * Hook for managing hidden widget state
 */
export function useHiddenWidgets() {
  const [hidden, setHidden] = useState<Set<WidgetId>>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      return stored ? new Set(JSON.parse(stored) as WidgetId[]) : new Set()
    } catch {
      return new Set()
    }
  })

  const hide = useCallback((id: WidgetId) => {
    setHidden((prev) => {
      const next = new Set(prev).add(id)
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...next]))
      return next
    })
  }, [])

  const show = useCallback((id: WidgetId) => {
    setHidden((prev) => {
      const next = new Set(prev)
      next.delete(id)
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...next]))
      return next
    })
  }, [])

  const showAll = useCallback(() => {
    setHidden(new Set())
    localStorage.removeItem(STORAGE_KEY)
  }, [])

  const isHidden = useCallback((id: WidgetId) => hidden.has(id), [hidden])

  return {
    hidden,
    hiddenCount: hidden.size,
    hide,
    show,
    showAll,
    isHidden,
  }
}
